/**
 * CheckmarkDraw — SVG checkmark that draws itself on trigger
 * Includes expanding glow ring and bounce-in wrapper.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface CheckmarkDrawProps {
  trigger: boolean;
  size?: number;
  color?: string;
}

export default function CheckmarkDraw({
  trigger,
  size = 48,
  color = 'var(--kid-meadow)',
}: CheckmarkDrawProps) {
  const reduced = useReducedMotion();

  return (
    <AnimatePresence>
      {trigger && (
        <motion.div
          className="relative inline-flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={reduced ? { duration: 0.15 } : springs.bouncy}
          style={{ width: size, height: size }}
        >
          {/* Glow ring */}
          {!reduced && (
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ border: `2px solid ${color}` }}
              initial={{ scale: 0.5, opacity: 0.8 }}
              animate={{ scale: 1.5, opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          )}

          {/* Checkmark SVG */}
          <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
            <motion.path
              d="M12 24 L21 33 L36 15"
              stroke={color}
              strokeWidth={4}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: reduced ? 0.1 : 0.4, ease: 'easeOut' }}
            />
          </svg>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
